<script setup>
import{ ref, onMounted, onUnmounted } from 'vue';
import { fetchLicenseById, createLicense, updateLicense } from '@/services/api';
import { useRouter } from 'vue-router';
import { useToast } from '@/composables/use-toast';
import { useLayoutComponents } from '@/composables/layout.js';
import Modal from '@/components/modals/Modal.vue';
import SelectInput from '@/components/forms/inputs/select-input.vue';
import { fetchLicensesOptions, deleteLicense as deleteLicenseService } from '@/services/licenseService';

const { TextInput,PrimaryButton, ButtonLoading, Checkbox, MarkdownRenderer } = useLayoutComponents();
const router = useRouter();
const { addToast } = useToast();

const license_id = ref(null);
const name = ref('');
const content = ref('');
const allow_additions = ref(false);
const isSubmitting = ref(false);
const tags = ref([]);
const short_description = ref('');
const newTag = ref('');
const listingLicenses = ref([]);
const licenses = ref([]);
const newLicense = ref('');
const showDeleteLicenseModal = ref(false);

const handleSuccess = async () => {
    isSubmitting.value = true;
    const values = {
        name: name.value,
        content: content.value,
        allow_additions: allow_additions.value,
        short_description: short_description.value,
        tags: tags.value
    };
    try{
        const result = license_id.value ? await updateLicense(license_id.value, values) : await createLicense(values);
        if(result) {
            addToast(`License ${license_id.value ? 'updated' : 'created'} successfully`, 'success');
            router.push('/backoffice/licenses');
        } else {
            addToast('Error saving license', 'error');
        }
    } catch (error) {
        addToast(error.message, 'error');
    }
    isSubmitting.value = false;
}

const addTag = () => {
    tags.value.push(newTag.value);
    newTag.value = '';
}

const removeTag = (tag) => {
    tags.value = tags.value.filter(t => t !== tag);
}

const handleDeleteLicense = async () => {
    if(listingLicenses.value.length > 0) {
        const fetchedLicenses = await fetchLicensesOptions();
        
        licenses.value = fetchedLicenses
            .filter(license => license.id !== license_id.value)
            .map(license => ({  
                label: license.name,
                value: license.id
            }));
    }
    
    showDeleteLicenseModal.value = true;
}

const confirmDeleteLicense = async () => {
    try {
        const new_license_id = listingLicenses.value.length > 0 ? newLicense.value : null;
        
        const result = await deleteLicenseService(license_id.value, new_license_id);
        if(result) {
            addToast('License deleted successfully', 'success');
            showDeleteLicenseModal.value = false;
            router.push('/backoffice/licenses');
        } 
    } catch (error) {
        addToast(error.message, 'error');
    }
}

onMounted(async () => {
    const tempLicenseId = localStorage.getItem('temp_editing_license_id');
    if(tempLicenseId) {
        const license = await fetchLicenseById(tempLicenseId);
        license_id.value = tempLicenseId;
        name.value = license.name;
        content.value = license.content;
        allow_additions.value = license.allow_additions;
        short_description.value = license.short_description ?? '';
        tags.value = license.tags ?? [];
        listingLicenses.value = license.listingLicenses;
        localStorage.removeItem('temp_editing_license_id');
    }
});

onUnmounted(() => {
    newLicense.value = '';
});

</script>

<template>
    <Modal :isOpen="showDeleteLicenseModal" title="Delete License" size="lg" @close="showDeleteLicenseModal = false">
        <div class="flex flex-col gap-4">
            <div v-if="listingLicenses.length > 0">
                <p class="text-white/90 mb-4 text-sm 2xl:text-base">
                    This license is being used in <strong>{{ listingLicenses.length }}</strong> listing(s). 
                    To delete this license, you need to select another license to replace it in those listings.
                </p>
                <div class="flex flex-col gap-2">
                    <SelectInput 
                        label="Select a new license" 
                        v-model="newLicense" 
                        :options="licenses" 
                        placeholder="Select a license..."
                    />
                </div>
                <div class="flex justify-end gap-2 mt-4">
                    <button 
                        @click="showDeleteLicenseModal = false" 
                        class="text-sm font-medium rounded-md px-4 py-2 border border-echoes-border-light text-white/80 hover:text-white hover:bg-white/5"
                    >
                        Cancel
                    </button>
                    <button 
                        @click="confirmDeleteLicense" 
                        :disabled="!newLicense"
                        class="text-sm font-medium rounded-md px-4 py-2 border border-echoes-border-light text-red-500 hover:text-red-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline"
                    >
                        Delete
                    </button>
                </div>
            </div>
            <div v-else>
                <p class="text-white/90 mb-4 text-sm 2xl:text-base">  
                    Are you sure you want to delete this license?
                </p>
                <div class="flex justify-end gap-2">
                    <button 
                        @click="showDeleteLicenseModal = false" 
                        class="text-sm font-medium rounded-md px-4 py-2 border border-echoes-border-light text-white/80 hover:text-white hover:bg-white/5"
                    >
                        Cancel
                    </button>
                    <button 
                        @click="confirmDeleteLicense" 
                        class="text-sm font-medium rounded-md px-4 py-2 border border-echoes-border-light text-red-500 hover:text-red-600 hover:underline"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    </Modal>
    <main class="p-5 bg-echoes-background-black flex flex-col gap-8">
        <div class="mt-4">
            <router-link to="/backoffice/licenses" class="text-sm text-white/80 hover:underline hover:text-white"><i class="mdi mdi-arrow-left mr-1"></i>Back to licenses</router-link>
            <h1 class="text-2xl font-semibold text-white">{{ license_id ? `Edit ${name} License` : 'Create License' }}</h1>
        </div>
        
    <div class="flex flex-col gap-4">
        <div class="col-span-1 grid grid-cols-3 gap-4">
            <div class="flex flex-col gap-4 mt-4">
                <div class="col-span-1">
                    <div class="form-label dark:text-white text-sm">Name</div>
                    <TextInput label="Name" v-model="name" :required="true" placeholder="Enter name" />
                </div>
                <div class="col-span-2 flex flex-col gap-2">
                    <div class="form-label dark:text-white text-sm">Tags</div>
                    <div class="flex flex-wrap gap-2">
                        <div v-for="tag in tags" :key="tag"  class="text-sm text-echoes-background-gray bg-white/70 rounded-full px-2 font-semibold leading-none py-1">
                            {{ tag }}
                            <button @click="removeTag(tag)" class="ml-2 hover:text-echoes-primary"><i class="mdi mdi-close"></i></button>
                        </div>
                    </div>
                    <div class="grid grid-cols-12 gap-2">
                        <TextInput label="Tags" v-model="newTag" :required="true" placeholder="Enter tag name" class="col-span-11" @keydown.enter="addTag" />
                        <div class="col-span-1 flex items-center">
                            <button @click="addTag" class="py-0.5 px-1 border border-echoes-border-gray rounded-lg hover:bg-echoes-background-gray"><i class="mdi mdi-plus"></i></button>
                        </div>
                    </div>
                </div>
                <div class="col-span-3">
                    <div class="form-label dark:text-white text-sm">Short Description {{ short_description ? short_description?.length : 0 }}/300</div>
                    <textarea class="text-area__field" v-model="short_description"
                        placeholder="Add a short description for your license (max 300 characters)"
                        maxlength="300" rows="3" />
                </div>
            </div>
    
            <div class="col-span-2 flex flex-col mt-4">
                <div class="form-label dark:text-white mb-2 flex items-center gap-2 relative pb-2">
                    <div>Content</div>
                    <div class="view-license-button">
                        <span class="tooltip" v-pre>Make sure to add <span class="font-bold">{{duration}}</span> into the license content to capture a defined license term and <span class="font-bold">{{additional_clauses}}</span> if the license accepts custom terms</span>
                        <i class="uil uil-info-circle text-lg"></i>
                    </div>
                </div>
                <div class="overflow-y-auto max-h-[60vh]">
                    <MarkdownRenderer v-model="content" placeholder="Write your content here..." :enableImageUpload="false"/>
                </div>
            </div>
        </div>

        <div class="flex gap-4 mb-2" :class="{'justify-end': !license_id, 'justify-between': license_id}">
            <div v-if="license_id">
                <button @click="handleDeleteLicense" class="text-sm font-medium rounded-md px-2 py-1 border border-echoes-border-light text-red-500 hover:text-red-600 hover:underline">Delete License</button>
            </div>
            <div class="flex items-center gap-3">
                <Checkbox v-model:checked="allow_additions" />
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Allow additional clauses</p>
                <PrimaryButton :loading="isSubmitting" :disabled="isSubmitting" @click="handleSuccess">
                    <ButtonLoading v-if="isSubmitting" size="sm" :text="isSubmitting ? 'Saving...' : 'Save'" />
                    <span v-else>Save</span>
                </PrimaryButton>
            </div>
        </div>

    </div>
        
    </main>
</template>

<style lang="postcss">
.text-area__field {

    @apply w-full  text-sm px-4 py-3 bg-echoes-background-black text-white border border-echoes-border-gray rounded-[13px] placeholder-white/70 focus:outline-none  focus:border-echoes-primary transition-all duration-200;

    &::placeholder {
        @apply transition-all duration-200 text-white/50 hover:text-white/80;
    }

    &:hover {
        @apply bg-echoes-background-black
        border-white/20
        text-white
        ;
    }

    &:focus::placeholder {
        @apply opacity-0 translate-x-1;
    }   

    &:focus {
        @apply border-echoes-primary;
    }

    &:disabled {
        @apply cursor-not-allowed opacity-60 bg-echoes-background-gray/30;
    }
}

/* Add smooth transition for tooltip */
.transition-transform {
  transition: transform 0.15s ease-in-out;
}

.tooltip {
    @apply absolute left-1/2 -translate-x-1/2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg whitespace-normal w-max max-w-[300px] border border-gray-700;
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 9999;
    bottom: calc(100% + 8px);
    pointer-events: none;
  }
  
  .tooltip::after {
    content: '';
    @apply absolute left-1/2 -translate-x-1/2 border-4 border-transparent;
    border-top-color: #374151;
    bottom: -8px;
  }
  
  .view-license-button:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }


.view-license-button {
    @apply text-white/80 hover:text-echoes-primary transition-colors relative inline-flex items-center;
  }
</style>